import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X, RotateCcw, Save } from "lucide-react";
import {
  ColunaCustom,
  mesclarColunasComBase,
  salvarColunasProjeto,
  restaurarColunasProjeto,
} from "../../services/projetoColunas";
import { listarColunasKanban } from "../../services/colunasKanban";
import { corContraste } from "../../components/TagChip";
import { useConfirm } from "../../components/ConfirmProvider";
import { useEscape } from "../../hooks/useEscape";

/**
 * Modal pra customizar visualmente as 5 colunas do kanban de UM projeto
 * (label, cor, emoji). Status interno é fixo — só renomeia.
 */
export function ColunasKanbanModal({ projetoId, customsAtuais, onClose }: {
  projetoId: string;
  customsAtuais: ColunaCustom[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  useEscape(true, onClose);

  // Base = catálogo global de colunas (ordenado). Merge dos customs por cima.
  const { data: colunasGlobais = [] } = useQuery({
    queryKey: ["colunas-kanban", "ativas"],
    queryFn: () => listarColunasKanban(),
  });
  const base: ColunaCustom[] = colunasGlobais.map((c) => ({ status: c.chave, label: c.label, cor: c.cor, emoji: c.emoji }));

  const [colunas, setColunas] = useState<ColunaCustom[]>([]);
  const [hidratado, setHidratado] = useState(false);
  useEffect(() => {
    if (!hidratado && base.length > 0) {
      setColunas(mesclarColunasComBase(base, customsAtuais));
      setHidratado(true);
    }
  }, [base, customsAtuais, hidratado]);

  function defDe(status: string): ColunaCustom {
    return base.find((b) => b.status === status) || { status, label: status, cor: "#94a3b8", emoji: "" };
  }

  function atualizar(idx: number, patch: Partial<ColunaCustom>) {
    setColunas((arr) => arr.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  }

  function restaurarLinha(idx: number) {
    setColunas((arr) => arr.map((c, i) => (i === idx ? { ...defDe(c.status) } : c)));
  }

  const salvarM = useMutation({
    mutationFn: () => salvarColunasProjeto(projetoId, colunas),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projeto-colunas", projetoId] });
      onClose();
    },
  });

  const restaurarTudoM = useMutation({
    mutationFn: () => restaurarColunasProjeto(projetoId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projeto-colunas", projetoId] });
      onClose();
    },
  });

  async function aoRestaurarTudo() {
    const ok = await confirm({
      title: "Restaurar padrões?",
      message: "Todas as 5 colunas voltam ao visual padrão do sistema. As tarefas não são afetadas.",
      confirmText: "Restaurar",
      danger: true,
    });
    if (ok) restaurarTudoM.mutate();
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-900 rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-200 dark:border-slate-700">
          <div>
            <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100">
              Personalizar colunas do kanban
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Só muda como o kanban deste projeto aparece. O resto do sistema (lista geral,
              dashboard, portal do cliente) continua com os labels padrão.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {colunas.map((col, idx) => {
            const def = defDe(col.status);
            const ehDefault = col.label === def.label && col.cor === def.cor && col.emoji === def.emoji;
            const txt = corContraste(col.cor) === "white" ? "#fff" : "#0f172a";
            return (
              <div key={col.status} className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase font-mono text-slate-400 shrink-0 w-32">{col.status}</span>
                  <input
                    type="color"
                    value={col.cor}
                    onChange={(e) => atualizar(idx, { cor: e.target.value })}
                    className="w-10 h-8 rounded cursor-pointer border border-slate-300"
                    title="Cor"
                  />
                  <input
                    type="text"
                    value={col.emoji}
                    onChange={(e) => atualizar(idx, { emoji: e.target.value })}
                    placeholder="(emoji)"
                    maxLength={4}
                    className="w-14 h-8 rounded text-center border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-base"
                    title="Emoji opcional"
                  />
                  <input
                    type="text"
                    value={col.label}
                    onChange={(e) => atualizar(idx, { label: e.target.value })}
                    placeholder={def.label}
                    className="flex-1 min-w-[160px] px-2 py-1.5 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none focus:border-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => restaurarLinha(idx)}
                    disabled={ehDefault}
                    className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1 disabled:opacity-40"
                    title="Restaurar esta coluna ao padrão"
                  >
                    <RotateCcw size={12} /> Padrão
                  </button>
                </div>
                {/* Preview */}
                <div className="flex items-center gap-2 pl-32 text-[11px] text-slate-500">
                  Preview:
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium"
                    style={{ background: col.cor, color: txt }}
                  >
                    {col.emoji && <span>{col.emoji}</span>}
                    <span>{col.label || def.label}</span>
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-t border-slate-200 dark:border-slate-700 px-5 py-3 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={aoRestaurarTudo}
            disabled={restaurarTudoM.isPending}
            className="text-xs text-red-600 hover:text-red-700 inline-flex items-center gap-1 disabled:opacity-50"
          >
            <RotateCcw size={12} /> Restaurar tudo
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="text-sm px-3 py-1.5 rounded bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-100"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => salvarM.mutate()}
              disabled={salvarM.isPending}
              className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded inline-flex items-center gap-1 disabled:opacity-50"
            >
              <Save size={14} /> {salvarM.isPending ? "Salvando…" : "Salvar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
